import React, { useState, useContext, useEffect, useRef } from 'react'
import ReactGA from 'react-ga'
import { useHistory, useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/react'

import {
  format,
  addDays,
  subDays,
  startOfDay,
  endOfDay,
  parseISO,
  startOfToday
} from 'date-fns'
import ptBr from 'date-fns/locale/pt-BR'
import queryString from 'query-string'
import useSWR from 'swr'
import Alert from '../components/Form/Alert'
import calendar from '../../../assets/icons/calendar.svg'
import ItemList from '../../../components/ItemList'
import { RootContext } from '../../../context/rootContext'
import {
  BoxContainer,
  Header,
  Month,
  Day,
  Navigation,
  DayNumber,
  ButtonLeft,
  ButtonRight,
  List,
  Message
} from './styles'
import SideMenuTemplate from '../templates/SideMenu'

const DailyView = () => {
  const history = useHistory()
  const dateQueryParam = new URLSearchParams(useLocation().search).get('date')
  const { loadingState, globalState } = useContext(RootContext)
  const [, setLoading] = loadingState
  const [state] = globalState
  const [isSticky, setSticky] = useState(false)
  const ref = useRef(null)
  const { partner, professional } = JSON.parse(localStorage.getItem('userData'))
  const [date, setDate] = useState(
    dateQueryParam ? parseISO(dateQueryParam) : startOfToday()
  )
  const [day, setDay] = useState(format(date, 'd', { locale: ptBr }))
  const [itemsList, setItemsList] = useState(undefined)

  const schedulingsParams = queryString.stringify({
    date: format(date, 'yyyy-MM-dd'),
    partnerId: partner,
    professionalId: professional.id
  })

  const { data: schedulings, error: schedulingsError } = useSWR(
    `${process.env.REACT_APP_SCHEDULER_URL}/schedulings/professional?${schedulingsParams}`
  )

  const blocksParams = queryString.stringify(
    {
      from: format(startOfDay(date), 'yyyy-MM-dd HH:mm'),
      to: format(endOfDay(date), 'yyyy-MM-dd HH:mm'),
      professionalIds: [professional.id]
    },
    { arrayFormat: 'bracket' }
  )

  const { data: blocks = [], error: blocksError } = useSWR(
    `${process.env.REACT_APP_PARTNER_URL}/block/occurrences?${blocksParams}`
  )

  const { data: overbooks = [] } = useSWR(() => `${process.env.REACT_APP_SCHEDULER_URL}/overbooks/professional?${queryString.stringify({
    date: format(date, 'yyyy-MM-dd'),
    partnerId: partner,
    professionalId: professional.id
  }, { arrayFormat: 'bracket' })}`)

  const orderByDate = array => {
    return array.sort(function (a, b) {
      return new Date(a.date) - new Date(b.date)
    })
  }

  const handleScroll = () => {
    if (ref.current) {
      setSticky(ref.current.getBoundingClientRect().top <= 0)
    }
  }

  const handleNextDay = () => {
    const newDate = addDays(date, 1)
    setDate(newDate)
  }

  const handlePrevDay = () => {
    const newDate = subDays(date, 1)
    setDate(newDate)
  }

  useEffect(() => {
    !itemsList ? setLoading(true) : setLoading(false)
  }, [itemsList, setLoading])

  useEffect(() => {
    if (blocks && schedulings && overbooks) {
      const newItemsListOrdened = orderByDate([
        ...blocks.map(block => {
          return {
            id: block.id,
            date: block.start,
            end: block.end,
            block: true,
            solicitation: getSolicitationByReference(block.id)
          }
        }),
        ...schedulings.map(scheduling => ({ ...scheduling, solicitation: getSolicitationByReference(scheduling.id) })),
        ...overbooks.map(overbook => ({ ...overbook, isOverbook: true, solicitation: getSolicitationByReference(overbook.id) }))
      ])

      if (JSON.stringify(itemsList) !== JSON.stringify(newItemsListOrdened)) {
        setItemsList(newItemsListOrdened)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks, schedulings, overbooks, itemsList, state.solicitationsList])

  const getSolicitationByReference = (referenceId) => {
    const solicitation = state.solicitationsList.find(solicitation => solicitation.referenceId === referenceId)
    return solicitation || {}
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', () => handleScroll)
    }
  }, [])

  useEffect(() => {
    if (date) {
      setDay(format(date, 'd', { locale: ptBr }))
      history.push({
        pathname: '/vets/calendar/daily',
        search: `?date=${format(date, 'yyyy-MM-dd')}`
      })
      ReactGA.pageview(`/vets/calendar/daily?Date=${format(date, 'yyyy-MM-dd')}`)
    }
  }, [date, history])

  if (schedulingsError) {
    Sentry.captureException(schedulingsError)
    return schedulingsError.toString()
  }

  if (blocksError) {
    Sentry.captureException(blocksError)
    return blocksError.toString()
  }

  return (
    <SideMenuTemplate>
      <Alert type='lock' show>
        Este calendário é privado e outros veterinários da clínica não tem acesso. Acompanhe nesta área apenas seus próprios atendimentos.
      </Alert>
      <Header isSticky={isSticky}>
        <Month to={`/vets/calendar/monthly?date=${format(date, 'yyyy-MM')}`}>
          <img src={calendar} alt="calendário" />
          <span>{format(date, 'MMMM', { locale: ptBr })}</span>
        </Month>
        <Day>
          <span>{format(date, 'EEE', { locale: ptBr })}</span>
          <Navigation>
            <ButtonLeft onClick={handlePrevDay} />
            <DayNumber>{day}</DayNumber>
            <ButtonRight onClick={handleNextDay} />
          </Navigation>
        </Day>
      </Header>
      <BoxContainer ref={ref} bg='#E0F8F9'>
        <List>
          {itemsList && itemsList.length === 0 && (
            <Message>Nenhum agendamento existente</Message>
          )}

          {itemsList &&
            itemsList.length > 0 &&
            itemsList.map(item => <ItemList key={item.id} item={item} />)}
        </List>
      </BoxContainer>
    </SideMenuTemplate>
  )
}

export default DailyView
